import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";

/** プロセスモデルに含まれるファイル 1 件。 */
export type ModelFile = {
  readonly name: string;
  readonly type: string;
  readonly size: number | null;
};

export type ProcessModelDetail = {
  readonly processModelId: string;
  readonly displayName: string;
  readonly description: string | null;
  /** モデルの主 BPMN と、その中の実行対象プロセス。 */
  readonly primaryFileName: string | null;
  readonly primaryProcessId: string | null;
  /** BPMN / DMN（図として開けるもの）。 */
  readonly diagrams: readonly ModelFile[];
  /** フォーム定義（JSON Schema / UI Schema）。 */
  readonly forms: readonly ModelFile[];
  /** 上記以外（README.md は除く。説明として別に扱う）。 */
  readonly others: readonly ModelFile[];
  /** 案件一覧・詳細に出す項目の宣言（metadata_extraction_paths）。 */
  readonly metadataPaths: readonly {
    readonly key: string;
    readonly path: string;
  }[];
};

export type ProcessModelFile = {
  readonly name: string;
  readonly type: string;
  readonly contents: string;
  /** 読み込んだ時点の内容のハッシュ。保存時の競合判定に使う。 */
  readonly contentsHash: string | null;
};

type SpiffModelFileRow = {
  readonly name: string;
  readonly type?: string | null;
  readonly size?: number | null;
};

const DIAGRAM_TYPES = new Set(["bpmn", "dmn"]);

/**
 * モデルの説明。SpiffArena 標準UI と同じモデル直下の README.md で、画面では
 * 「モデルの説明」として見せる（model-readme.tsx / process-description.tsx が
 * この定数で読む）。ファイル一覧にも出すと同じものが二重に見えるので、一覧からは外す。
 */
export const README_FILE = "README.md";

/**
 * フォーム定義（json）。項目を定義する `*-schema.json` と、並び順などを指定する
 * `*-uischema.json` はどちらもフォームの一部なので同じ束にする
 * （どちらも設計画面で編集する対象）。
 */
function isFormFile(file: ModelFile): boolean {
  return file.type === "json";
}

/**
 * プロセスモデルの詳細。ファイルは用途ごとに分けて返す
 * （画面が種別を判定し直さなくて済むように）。
 */
export function processModelQueryOptions(
  client: ApiClient,
  processModelId: string,
) {
  const modifiedId = toModifiedProcessModelId(processModelId);

  const fetchModel = async (): Promise<ProcessModelDetail> => {
    const model = await client.get<{
      readonly id: string;
      readonly display_name: string;
      readonly description?: string | null;
      readonly primary_file_name?: string | null;
      readonly primary_process_id?: string | null;
      readonly files?: readonly SpiffModelFileRow[];
      readonly metadata_extraction_paths?: readonly {
        readonly key: string;
        readonly path: string;
      }[];
    }>(`/process-models/${modifiedId}`);

    const files = (model.files ?? []).map<ModelFile>((file) => ({
      name: file.name,
      type: file.type ?? "",
      size: file.size ?? null,
    }));

    return {
      processModelId: model.id,
      displayName: model.display_name,
      description:
        model.description === undefined || model.description === ""
          ? null
          : model.description,
      primaryFileName: model.primary_file_name ?? null,
      primaryProcessId: model.primary_process_id ?? null,
      diagrams: files.filter((file) => DIAGRAM_TYPES.has(file.type)),
      forms: files.filter(isFormFile),
      others: files.filter(
        (file) =>
          !DIAGRAM_TYPES.has(file.type) &&
          !isFormFile(file) &&
          file.name !== README_FILE,
      ),
      metadataPaths: model.metadata_extraction_paths ?? [],
    };
  };

  return queryOptions({
    queryKey: ["process-models", "detail", modifiedId] as const,
    queryFn: fetchModel,
    staleTime: 60_000,
  });
}

/** モデル内の 1 ファイルの内容（BPMN XML / JSON Schema など）。 */
export function processModelFileQueryOptions(
  client: ApiClient,
  processModelId: string,
  fileName: string,
) {
  const modifiedId = toModifiedProcessModelId(processModelId);

  const fetchFile = async (): Promise<ProcessModelFile> => {
    const file = await client.get<{
      readonly name: string;
      readonly type?: string | null;
      readonly file_contents?: string | null;
      readonly file_contents_hash?: string | null;
    }>(`/process-models/${modifiedId}/files/${fileName}`);

    return {
      name: file.name,
      type: file.type ?? "",
      contents: file.file_contents ?? "",
      contentsHash: file.file_contents_hash ?? null,
    };
  };

  return queryOptions({
    queryKey: ["process-models", "file", modifiedId, fileName] as const,
    queryFn: fetchFile,
    staleTime: 60_000,
  });
}

export function useProcessModel(
  processModelId: string,
): UseQueryResult<ProcessModelDetail> {
  return useQuery(processModelQueryOptions(useApiClient(), processModelId));
}

export function useProcessModelFile(
  processModelId: string,
  fileName: string | null,
): UseQueryResult<ProcessModelFile> {
  const client = useApiClient();
  return useQuery({
    ...processModelFileQueryOptions(client, processModelId, fileName ?? ""),
    enabled: fileName !== null,
  });
}
